import { useEffect, useMemo, useState } from 'react';
import { useGamesStore } from '../stores/gamesStore';

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M19 19L14.667 14.667M17 9.5C17 13.6421 13.6421 17 9.5 17C5.35786 17 2 13.6421 2 9.5C2 5.35786 5.35786 2 9.5 2C13.6421 2 17 5.35786 17 9.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M12.667 4V13.333C12.667 13.702 12.368 14 12 14H4C3.632 14 3.333 13.702 3.333 13.333V4" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const STATUS_LABELS = {
  all: 'Все',
  finished: 'Завершена',
  active: 'Идёт',
  abandoned: 'Брошена',
  expired: 'Истекла',
};

const STATUS_STYLES = {
  finished: 'bg-success/10 text-success',
  active: 'bg-accent-primary/10 text-accent-primary',
  abandoned: 'bg-text-muted/10 text-text-secondary',
  expired: 'bg-error/10 text-error',
};

const REJECTION_LABELS = {
  IMPLAUSIBLE: 'неправдоподобный темп',
};

const numberInputClass =
  'w-full px-3 py-2 bg-bg-tertiary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary';

const fieldLabelClass = 'block text-xs font-medium text-text-secondary mb-1';

// Формат даты из MySQL -> локальное время, иначе браузер покажет UTC
// и время «уедет» на несколько часов назад.
const parseDateTime = (value) => {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseDateTime(value);
  return date ? date.toLocaleString('ru-RU') : '—';
};

// Сколько времени заняла партия: от старта до закрытия сессии.
//
// Считаем только по двум реальным меткам времени. Для активных сессий
// длительность от «сейчас» смотреть нельзя: брошенная сессия может
// висеть сутки, и таймер покажет «30 ч» вместо реальных двух минут.
const formatDuration = (startedAt, finishedAt) => {
  const start = parseDateTime(startedAt);
  const end = parseDateTime(finishedAt);
  if (!start || !end) return null;

  const total = Math.round((end - start) / 1000);
  if (total < 0) return null;

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) return minutes > 0 ? `${hours}ч ${minutes}мин` : `${hours}ч`;
  if (minutes > 0) return seconds > 0 ? `${minutes}мин ${seconds}сек` : `${minutes}мин`;
  return `${seconds} сек`;
};

// ============================================
// Сводка
// ============================================

const SummaryCards = ({ summary }) => {
  const cards = [
    { label: 'Партий', value: summary.runs.toLocaleString('ru-RU') },
    { label: 'Выдано звёзд', value: summary.stars.toLocaleString('ru-RU'), accent: true },
    { label: 'Игроков', value: summary.players.toLocaleString('ru-RU') },
    { label: 'Суммарный счёт', value: summary.score.toLocaleString('ru-RU') },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-bg-secondary rounded-xl border border-border-color p-4">
          <div className="text-xs text-text-secondary">{card.label}</div>
          <div className={`text-2xl font-bold mt-1 ${card.accent ? 'text-accent-primary' : 'text-text-primary'}`}>
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const TopPlayers = ({ players }) => {
  if (!players || players.length === 0) return null;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-4">
      <div className="text-xs text-text-secondary mb-3">Кто играл активнее всего</div>
      <div className="flex flex-wrap gap-2">
        {players.map((player) => (
          <div
            key={player.vk_user_id}
            className="flex items-center gap-2 px-3 py-1.5 bg-bg-tertiary rounded-lg text-sm"
          >
            <span className="font-mono text-text-primary">{player.vk_user_id}</span>
            <span className="text-text-secondary">{player.runs} пар.</span>
            <span className="text-accent-primary font-medium">{player.stars} ⭐</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// Вкладка «Партии»
// ============================================

const SessionsTab = ({ games }) => {
  const {
    sessions,
    summary,
    total,
    page,
    setPage,
    totalPages,
    isLoadingSessions,
    fetchSessions,
    fetchSummary,
  } = useGamesStore();

  const [search, setSearch] = useState('');
  const [gameId, setGameId] = useState('');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const limit = 20;

  // Фильтры собираем в объект по значению: useEffect перезапускается
  // только когда реально изменился набор условий, а не каждый рендер.
  const filters = useMemo(
    () => ({ search, game_id: gameId, status, date_from: dateFrom, date_to: dateTo }),
    [search, gameId, status, dateFrom, dateTo]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchSessions({ ...filters, limit, offset: (page - 1) * limit });
      fetchSummary(filters);
    }, 300);

    return () => clearTimeout(debounce);
  }, [filters, page]);

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <SummaryCards summary={summary} />

      <TopPlayers players={summary.topPlayers} />

      {/* Фильтры */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => resetPage(setSearch)(e.target.value)}
            placeholder="Поиск по VK ID..."
            className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
            <SearchIcon />
          </div>
        </div>

        <select
          value={gameId}
          onChange={(e) => resetPage(setGameId)(e.target.value)}
          className="px-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
        >
          <option value="">Все игры</option>
          {games.map((game) => (
            <option key={game.game_id} value={game.game_id}>
              {game.title}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => resetPage(setStatus)(e.target.value)}
          className="px-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
        >
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <div>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => resetPage(setDateFrom)(e.target.value)}
            title="С"
            className="w-full px-3 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
          />
        </div>

        <div>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => resetPage(setDateTo)(e.target.value)}
            title="По"
            className="w-full px-3 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-text-secondary">
          Всего: <span className="text-text-primary font-medium">{total}</span>
        </div>
        {(search || gameId || status !== 'all' || dateFrom || dateTo) && (
          <button
            onClick={() => {
              setSearch('');
              setGameId('');
              setStatus('all');
              setDateFrom('');
              setDateTo('');
              setPage(1);
            }}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Сбросить фильтры
          </button>
        )}
      </div>

      {/* Таблица */}
      <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-tertiary border-b border-border-color">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Начало</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Конец</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">VK User ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Игра</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Счёт</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Звёзды</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-color">
              {isLoadingSessions ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                    <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                    Партий не найдено
                  </td>
                </tr>
              ) : (
                sessions.map((session) => {
                  const duration = formatDuration(session.started_at, session.finished_at);

                  return (
                  <tr key={session.id} className="hover:bg-bg-tertiary/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-text-muted font-mono">{session.id}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {formatDateTime(session.started_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{formatDateTime(session.finished_at)}</span>
                        {duration ? (
                          <span className="px-2 py-0.5 rounded text-xs bg-bg-tertiary text-text-secondary">
                            {duration}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-mono">{session.vk_user_id}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">
                      {session.game_title || session.game_id}
                      {session.is_new_record ? (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-400">
                          рекорд
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary text-right">
                      {session.score === null ? '—' : session.score}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={session.stars_awarded > 0 ? 'text-accent-primary font-medium' : 'text-text-muted'}>
                        {session.stars_awarded > 0 ? `+${session.stars_awarded}` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[session.status] || 'bg-text-muted/10 text-text-secondary'}`}>
                        {STATUS_LABELS[session.status] || session.status}
                      </span>
                      {session.rejection_code ? (
                        <div className="text-xs text-text-muted mt-1">
                          {REJECTION_LABELS[session.rejection_code] || session.rejection_code}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            Страница {page} из {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoadingSessions}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Назад
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoadingSessions}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Вперёд
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Вкладка «Награды»
// ============================================

const emptyTier = () => ({ id: 0, min_score: '', stars: '' });

const toTierValue = (value) => (value === '' || value === null ? '' : String(value));

const GameCard = ({ game, onSave, isSaving }) => {
  // Черновик: правки не уходят в базу, пока не нажата «Сохранить».
  const [draft, setDraft] = useState(() => ({
    ...game,
    tiers: game.tiers.map((tier) => ({ ...tier })),
  }));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setDraft({ ...game, tiers: game.tiers.map((tier) => ({ ...tier })) });
    setLocalError(null);
  }, [game]);

  const updateField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setLocalError(null);
  };

  const updateTier = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      tiers: current.tiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)),
    }));
    setLocalError(null);
  };

  const addTier = () => {
    setDraft((current) => ({
      ...current,
      tiers: [...current.tiers, emptyTier()].sort((a, b) => {
        const aScore = Number(a.min_score) || 0;
        const bScore = Number(b.min_score) || 0;
        return aScore - bScore;
      }),
    }));
  };

  const removeTier = (index) => {
    setDraft((current) => ({
      ...current,
      tiers: current.tiers.filter((_, i) => i !== index),
    }));
    setLocalError(null);
  };

  const handleSave = async () => {
    const tiers = [];
    const seen = new Set();

    for (const tier of draft.tiers) {
      const minScoreRaw = toTierValue(tier.min_score);
      const starsRaw = toTierValue(tier.stars);

      if (minScoreRaw === '' || starsRaw === '') {
        setLocalError('Заполните счёт и награду в каждом пороге');
        return;
      }

      const minScore = Number(minScoreRaw);
      const stars = Number(starsRaw);

      if (!Number.isInteger(minScore) || minScore < 0) {
        setLocalError('Счёт должен быть целым неотрицательным числом');
        return;
      }

      if (!Number.isInteger(stars) || stars < 0) {
        setLocalError('Награда должна быть целым неотрицательным числом');
        return;
      }

      if (seen.has(minScore)) {
        setLocalError(`Два порога с одинаковым счётом (${minScore}) — оставьте один`);
        return;
      }

      seen.add(minScore);
      tiers.push({ id: tier.id || 0, min_score: minScore, stars });
    }

    if (tiers.length === 0) {
      setLocalError('Нужен хотя бы один порог, иначе игра не будет платить звёзды');
      return;
    }

    setLocalError(null);
    await onSave(game.game_id, {
      is_active: draft.is_active,
      max_stars_per_run: Number(draft.max_stars_per_run) || 0,
      max_stars_per_day: Number(draft.max_stars_per_day) || 0,
      session_ttl_minutes: Number(draft.session_ttl_minutes) || 0,
      max_score_per_minute: Number(draft.max_score_per_minute) || 0,
      tiers,
    });
  };

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border-color">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-text-primary">{game.title}</h2>
          <span className="text-xs font-mono text-text-muted">{game.game_id}</span>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="h-4 w-4 accent-accent-primary"
            />
            Игра доступна
          </label>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 rounded-lg bg-accent-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {localError && (
        <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-4 py-3 text-sm">
          {localError}
        </div>
      )}

      {/* Потолки и лимиты */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-6 py-4 border-b border-border-color">
        <div>
          <label className={fieldLabelClass} title="0 = брать награду из таблицы порогов">
            Потолок за партию
          </label>
          <input
            type="number"
            min="0"
            value={draft.max_stars_per_run}
            onChange={(e) => updateField('max_stars_per_run', e.target.value)}
            className={numberInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} title="0 = без суточного ограничения">
            Потолок за сутки
          </label>
          <input
            type="number"
            min="0"
            value={draft.max_stars_per_day}
            onChange={(e) => updateField('max_stars_per_day', e.target.value)}
            className={numberInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Время сессии, мин</label>
          <input
            type="number"
            min="1"
            value={draft.session_ttl_minutes}
            onChange={(e) => updateField('session_ttl_minutes', e.target.value)}
            className={numberInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} title="0 = отключить проверку правдоподобия">
            Счёт в минуту
          </label>
          <input
            type="number"
            min="0"
            value={draft.max_score_per_minute}
            onChange={(e) => updateField('max_score_per_minute', e.target.value)}
            className={numberInputClass}
          />
        </div>
      </div>

      {/* Пороги наград */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-text-secondary">
            Пороги наград: игрок получает звёзды самого высокого порога, который не превышает его счёт.
          </div>
          <button
            onClick={addTier}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-bg-tertiary text-text-secondary hover:text-text-primary rounded-lg transition-colors"
          >
            <PlusIcon />
            Добавить порог
          </button>
        </div>

        <div className="grid grid-cols-[1fr_1fr_44px] gap-3 px-3 py-2 border-b border-border-color text-xs font-medium text-text-secondary uppercase tracking-wider">
          <div>Счёт от</div>
          <div>Звёзд</div>
          <div />
        </div>

        <div className="divide-y divide-border-color">
          {draft.tiers.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-text-secondary">
              Порогов нет — игра не будет начислять звёзды
            </div>
          ) : (
            draft.tiers.map((tier, index) => (
              <div key={tier.id || `new-${index}`} className="grid grid-cols-[1fr_1fr_44px] gap-3 px-3 py-2 items-center">
                <input
                  type="number"
                  min="0"
                  value={toTierValue(tier.min_score)}
                  onChange={(e) => updateTier(index, 'min_score', e.target.value)}
                  className={numberInputClass}
                  placeholder="0"
                />
                <input
                  type="number"
                  min="0"
                  value={toTierValue(tier.stars)}
                  onChange={(e) => updateTier(index, 'stars', e.target.value)}
                  className={numberInputClass}
                  placeholder="0"
                />
                <button
                  onClick={() => removeTier(index)}
                  className="p-2 text-text-secondary hover:text-error transition-colors"
                  title="Удалить порог"
                >
                  <TrashIcon />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const RewardsTab = () => {
  const { games, isLoadingGames, saveGame } = useGamesStore();
  const [savingGameId, setSavingGameId] = useState(null);

  const handleSave = async (gameId, payload) => {
    setSavingGameId(gameId);
    await saveGame(gameId, payload);
    setSavingGameId(null);
  };

  if (isLoadingGames) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {games.map((game) => (
        <GameCard key={game.game_id} game={game} onSave={handleSave} isSaving={savingGameId === game.game_id} />
      ))}
    </div>
  );
};

// ============================================
// Страница
// ============================================

export const Games = () => {
  const { games, error, success, clearError, clearSuccess } = useGamesStore();
  const [tab, setTab] = useState('sessions');

  // Список игр нужен обоим табам: фильтру журнала и карточкам наград.
  useEffect(() => {
    useGamesStore.getState().fetchGames();
  }, []);

  const tabs = [
    { id: 'sessions', label: 'Партии' },
    { id: 'rewards', label: 'Награды' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Игры</h1>
        <p className="text-sm text-text-secondary mt-1">
          Кто играет, когда и во что, сколько за это заплачено звёзд, и настройка наград за достижения.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border-color">
        {tabs.map((item) => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === item.id
                ? 'border-accent-primary text-accent-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error && (
        <div
          onClick={clearError}
          className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-4 py-3 cursor-pointer"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          onClick={clearSuccess}
          className="bg-green-500/10 border border-green-500/30 text-green-300 rounded-xl px-4 py-3 cursor-pointer"
        >
          {success}
        </div>
      )}

      {tab === 'sessions' ? <SessionsTab games={games} /> : <RewardsTab />}
    </div>
  );
};
